import classNames from 'classnames'
import Text from 'components/Text'
import Button from 'components/Button'
import ImgStoryblok from 'components/ImgStoryblok'
import { Form, Formik } from 'formik'
import cpfMask from 'rv-utils/build/masks/cpf'
import cnpjMask from 'rv-utils/build/masks/cnpj'
import { InputText } from 'components/Fields'
import { navigate } from 'gatsby'
import segment from 'utils/segment'
import useMediaQuery from 'hooks/useMediaQuery'
import removeSpecialChars from 'rv-utils/build/formatters/removeSpecialChars'
import storage from 'utils/storage'
import './LandingHeroHoliday.styl'

import Carousel from './Carousel'

const EMPHASIS_REGEX = /({{(\w|\s|[áâãéêíóôõúçñÁÀÂÃÉÍÓÔÕÚÇ])+}})/gi

const HeroBanner = ({ blok, location, isInCarrossel, firstBanner }) => {
  const isNotMobile = useMediaQuery('notMobile')

  const renderText = ({ text, textColor, subText = false }) => {
    if (!text) return <></>

    let textToRender = text

    if (blok?.emphasis_color) {
      const isEmphasis = (text) => text?.match(EMPHASIS_REGEX)

      const splittedTextWithEmphasis = text.split(EMPHASIS_REGEX)

      const filteredEmphasisText = splittedTextWithEmphasis
        .map((text, index) => {
          if (index === 0) return text
          if (isEmphasis(splittedTextWithEmphasis[index - 1])) return
          return text
        })
        .filter((t) => t)

      const renderEmphasisText = (text, index) => {
        const emphasisText = text.replace(/({{|}})/gi, '')

        if (emphasisText === 'newline') return <br key={`breakLine${index}`} />

        return (
          <span
            className="emphasis-texts"
            style={{ color: blok?.emphasis_color }}
            key={`emphasisText${index}`}
          >
            {emphasisText}
          </span>
        )
      }

      textToRender = filteredEmphasisText.map((text, index) => {
        if (isEmphasis(text)) {
          return renderEmphasisText(text, index)
        }

        return text
      })
    }

    return (
      <div
        className={classNames('inline', {
          title_text: !subText,
          sub_text: subText,
        })}
        style={{ color: textColor }}
      >
        {textToRender}
      </div>
    )
  }

  const blokBgImages = {
    desktop: blok?.background_desktop?.filename?.replace(
      'https://a.storyblok.com',
      'https://assets.renegocie.cartoes.itau.com.br'
    ),
    mobile: blok?.background_mobile?.filename?.replace(
      'https://a.storyblok.com',
      'https://assets.renegocie.cartoes.itau.com.br'
    ),
  }
  const currentBlokImage = isNotMobile
    ? blokBgImages.desktop
    : blokBgImages.mobile

  const getClassNameWithModifier = (className, modifier) => {
    return `${className}--${modifier}`
  }

  return (
    <>
      <div
        className={classNames('landing-hero-holiday', {
          [getClassNameWithModifier(
            'landing-hero-holiday',
            blok?.mobile_content_position
          )]: !isNotMobile,
          'with-disclamer': blok?.disclamer_text,
        })}
        style={{
          backgroundColor: blok?.background_color || '#fff',
        }}
        data-test="container-hero-holiday"
      >
        <ImgStoryblok
          src={`${currentBlokImage}`}
          preload={!isNotMobile}
          alt={`${blok?.title_line1_text.replace(/[{}]/g, '')}`}
          pictureProps={{
            className: 'landing-hero-holiday__background',
            role: 'presentation',
            'aria-hidden': 'true',
          }}
        />

        <div
          className={classNames('left-part', {
            [getClassNameWithModifier(
              'left-part',
              blok?.mobile_content_position
            )]: !isNotMobile,
          })}
        >
          {(isNotMobile || blok?.show_main_title_mobile) && (
            <Text h2 size={1} className="label-text">
              negocie dívidas online
            </Text>
          )}

          <Text h1 size={3} className="main-text">
            {renderText({
              text: blok?.title_line1_text,
              textColor: blok?.title_line1_color,
            })}
            {renderText({
              text: blok?.title_line2_text,
              textColor: blok?.title_line2_color,
            })}
            {renderText({
              text: blok?.title_line3_text,
              textColor: blok?.title_line3_color,
              subText: true,
            })}
          </Text>

          <div className="form-container">
            <Formik
              initialValues={{ document: '' }}
              onSubmit={(values) => {
                const documentNumber = values.document
                const leadType =
                  documentNumber &&
                  removeSpecialChars(documentNumber).length > 11
                    ? 'PJ'
                    : 'PF'
                storage.session.setItem('leadType', leadType)

                segment.track('document_registration')
                if (typeof window !== 'undefined') {
                  window.onbeforeunload = null
                }
                navigate(`/registro`, {
                  state: {
                    document: values.document,
                    source_url: location.href,
                  },
                })
              }}
            >
              {({ ...formProps }) => {
                return (
                  <Form className="form-content">
                    <InputText
                      name="document"
                      placeholder="Digite seu CPF ou CNPJ"
                      mask={(e) => {
                        if (e && removeSpecialChars(e).length <= 11)
                          return cpfMask
                        else return cnpjMask
                      }}
                      rules={{ isCpfOrCnpj: true, isRequired: true }}
                      inputMode="numeric"
                      dataTest={
                        !isInCarrossel || firstBanner ? 'input-document' : ''
                      }
                      errorColor={blok?.error_text_color}
                      {...formProps}
                    />

                    <Button
                      className="button"
                      type="submit"
                      data-unique="63a2d907-4325-4e21-a45f-d420493b4f10"
                      data-test={
                        !isInCarrossel || firstBanner
                          ? 'button-submit-document'
                          : ''
                      }
                      style={{
                        backgroundColor: blok?.button_bg_color,
                        color: blok?.button_text_color,
                      }}
                    >
                      Consultar
                    </Button>
                  </Form>
                )
              }}
            </Formik>
          </div>
        </div>
      </div>

      {blok?.disclamer_text && (
        <Text
          p
          className={classNames('banner-campaign-text', {
            'banner-campaign-text--carrossel': isInCarrossel,
          })}
          style={{ color: blok?.disclamer_text_color }}
        >
          {blok?.disclamer_text}
        </Text>
      )}
    </>
  )
}

const LandingHeroHoliday = ({ blok, ...props }) => {
  const holidaySlides = blok?.holiday_slides

  if (!holidaySlides) return <></>

  if (holidaySlides.length === 1) {
    return <HeroBanner blok={holidaySlides[0]} {...props} />
  }

  return (
    <Carousel bullets arrows>
      {holidaySlides.map((slide, index) => (
        <HeroBanner
          key={slide._uid}
          firstBanner={index === 0}
          blok={slide}
          isInCarrossel
          {...props}
        />
      ))}
    </Carousel>
  )
}

export default LandingHeroHoliday
